import { useEffect, useMemo, useRef, useState } from 'react';
import imageCompression from 'browser-image-compression';
import DrawingCanvasModal from './DrawingCanvasModal';
import { IMAGE_INPUT_TYPE } from '../../utils/imageUpload';

function isCompactPreviewViewport() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.innerWidth <= 640;
}

function AnswerCapture({
  idPrefix,
  disabled,
  selectedFiles,
  onFilesReady,
  onFileReady,
  onRemoveFile,
  onClearFiles,
  onMoveFile,
  questionMarkdown,
  drawingDraftPages,
  onDrawingDraftChange,
  allowTypedAnswer = false,
  typedAnswer = '',
  onTypedAnswerChange,
}) {
  const [isDrawingOpen, setIsDrawingOpen] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [usesCompactPreviewLayout, setUsesCompactPreviewLayout] = useState(() => isCompactPreviewViewport());
  const [isPagesExpanded, setIsPagesExpanded] = useState(() => !isCompactPreviewViewport());
  const drawingButtonRef = useRef(null);
  const files = useMemo(
    () => (Array.isArray(selectedFiles) ? selectedFiles.filter(Boolean) : []),
    [selectedFiles]
  );
  const fileStatusText = useMemo(() => {
    if (!files.length) {
      return 'No files selected';
    }
    if (files.length === 1) {
      return `1 file selected: ${files[0]?.name || 'image'}`;
    }
    return `${files.length} files selected`;
  }, [files]);

  const previewUrls = useMemo(
    () =>
      files.map((file) => ({
        name: file?.name || 'image',
        url:
          typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
            ? URL.createObjectURL(file)
            : '',
      })),
    [files]
  );

  useEffect(() => {
    const syncViewport = () => {
      const compact = isCompactPreviewViewport();
      setUsesCompactPreviewLayout(compact);
      setIsPagesExpanded((current) => (compact ? false : current || true));
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    return () => window.removeEventListener('resize', syncViewport);
  }, []);

  useEffect(() => {
    if (!files.length) {
      setIsPagesExpanded(!usesCompactPreviewLayout);
      return;
    }
    if (!usesCompactPreviewLayout) {
      setIsPagesExpanded(true);
    }
  }, [files.length, usesCompactPreviewLayout]);

  useEffect(() => {
    return () => {
      previewUrls.forEach((preview) => {
        if (preview.url && preview.url.startsWith('blob:') && typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [previewUrls]);

  useEffect(() => {
    setPreviewIndex((current) => {
      if (current === null) {
        return current;
      }
      if (!previewUrls.length) {
        return null;
      }
      return Math.min(current, previewUrls.length - 1);
    });
  }, [previewUrls.length]);

  useEffect(() => {
    if (previewIndex === null) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewIndex(null);
        return;
      }

      if (event.key === 'ArrowRight') {
        setPreviewIndex((current) =>
          current === null ? null : Math.min(previewUrls.length - 1, current + 1)
        );
        return;
      }

      if (event.key === 'ArrowLeft') {
        setPreviewIndex((current) => (current === null ? null : Math.max(0, current - 1)));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewIndex, previewUrls.length]);

  const emitReady = (nextFiles, meta) => {
    if (typeof onFilesReady === 'function') {
      onFilesReady(nextFiles, meta);
      return;
    }
    if (typeof onFileReady === 'function') {
      const first = nextFiles?.[0] || null;
      onFileReady(first, meta);
    }
  };

  const isPreviewOpen = previewIndex !== null && previewIndex >= 0 && previewIndex < previewUrls.length;
  const currentPreview = isPreviewOpen ? previewUrls[previewIndex] : null;

  return (
    <div className="answer-capture">
      {allowTypedAnswer ? (
        <label className="stack-xs">
          <span className="field-label">Type your answer</span>
          <input
            id={`${idPrefix}_typed_answer`}
            type="text"
            inputMode="decimal"
            value={typedAnswer}
            onChange={(event) => onTypedAnswerChange?.(event.target.value)}
            disabled={disabled}
            aria-label="Type your answer"
          />
        </label>
      ) : null}
      <div className="capture-actions">
        <label className={`btn btn-primary capture-upload-trigger${disabled || isCompressing ? ' is-disabled' : ''}`}>
          <span>{isCompressing ? 'Preparing image...' : 'Upload Photo(s)'}</span>
          <input
            id={`${idPrefix}_upload`}
            type="file"
            accept="image/*"
            multiple
            className="capture-upload-input"
            aria-label="Upload Photo(s) files"
            disabled={disabled || isCompressing}
            onChange={async (event) => {
              const nextFiles = Array.from(event.target.files || []);
              event.target.value = '';
              if (!nextFiles.length) {
                return;
              }

              setIsCompressing(true);
              try {
                const compressedFiles = new Array(nextFiles.length);
                await Promise.all(
                  nextFiles.map(async (file, index) => {
                    const compressed = await imageCompression(file, {
                      maxSizeMB: 1.5,
                      maxWidthOrHeight: 2048,
                      useWebWorker: true,
                      initialQuality: 0.85,
                    });
                    compressedFiles[index] = new File([compressed], file.name, {
                      type: compressed.type || file.type,
                    });
                  })
                );
                emitReady(compressedFiles, { inputType: IMAGE_INPUT_TYPE.PHOTO });
              } catch (error) {
                emitReady(nextFiles, { inputType: IMAGE_INPUT_TYPE.PHOTO });
              } finally {
                setIsCompressing(false);
              }
            }}
          />
        </label>
        <button
          ref={drawingButtonRef}
          type="button"
          className="btn btn-secondary"
          disabled={disabled || isCompressing}
          onClick={() => {
            if (files.length) {
              if (!window.confirm('Discard the selected drawing and start a new one?')) return;
              onClearFiles?.();
              onDrawingDraftChange?.([]);
            }
            setIsDrawingOpen(true);
          }}
        >
          {files.length ? 'Start new drawing' : 'Draw answer pages'}
        </button>
      </div>

      <p className="muted-text capture-file-status" aria-live="polite">
        {fileStatusText}
      </p>

      {files.length ? (
        <div className="capture-file-header">
          <p className="capture-name">
            Selected pages: {files.length}
          </p>
          <button
            type="button"
            className="btn btn-tertiary"
            disabled={disabled}
            onClick={() => onClearFiles?.()}
          >
            Clear selection
          </button>
        </div>
      ) : null}

      {previewUrls.length ? (
        <div className="capture-preview-section">
          {previewUrls.length > 1 ? (
            <p className="muted-text capture-order-guidance">
              Pages will be read in this order. Move pages if needed before submitting.
            </p>
          ) : null}
          {usesCompactPreviewLayout ? (
            <div className="capture-preview-summary-row">
              <button
                type="button"
                className={`btn btn-tertiary capture-preview-summary-toggle${isPagesExpanded ? ' is-active' : ''}`}
                onClick={() => setIsPagesExpanded((current) => !current)}
                aria-expanded={isPagesExpanded}
              >
                {previewUrls.length} page{previewUrls.length === 1 ? '' : 's'} added
              </button>
            </div>
          ) : null}

          {isPagesExpanded ? (
            <div className="capture-preview-grid" data-testid="capture-preview-grid">
              {previewUrls.map((preview, index) => (
                <div className="capture-preview-item" key={`${preview.name}-${index}`}>
                  <div className={`capture-preview-tile${previewIndex === index ? ' is-active' : ''}`}>
                    {preview.url ? (
                      <button
                        type="button"
                        className={`capture-preview-thumb-btn${previewIndex === index ? ' is-active' : ''}`}
                        onClick={() => setPreviewIndex(index)}
                        aria-label={`Open page ${index + 1}`}
                      >
                        <img src={preview.url} alt={`Submission preview ${index + 1}`} />
                      </button>
                    ) : null}
                    <div className="capture-preview-overlay">
                      <span className="capture-preview-page-badge" title={preview.name}>Page {index + 1}</span>
                      <div className="capture-preview-overlay-actions">
                        <button
                          type="button"
                          className="btn btn-tertiary capture-overlay-icon-btn"
                          disabled={disabled || index === 0}
                          onClick={() => onMoveFile?.(index, 'up')}
                          aria-label={`Move page ${index + 1} up`}
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn btn-tertiary capture-overlay-icon-btn"
                          disabled={disabled || index === previewUrls.length - 1}
                          onClick={() => onMoveFile?.(index, 'down')}
                          aria-label={`Move page ${index + 1} down`}
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn btn-tertiary capture-overlay-icon-btn is-danger"
                          disabled={disabled}
                          onClick={() => onRemoveFile?.(index)}
                          aria-label={`Remove page ${index + 1}`}
                          title="Remove page"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {isPreviewOpen && currentPreview ? (
        <div
          className="preview-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setPreviewIndex(null);
            }
          }}
        >
          <div className="preview-modal-sheet">
            <div className="preview-modal-header">
              <p className="capture-name">Page {previewIndex + 1} of {previewUrls.length}</p>
              <button type="button" className="btn btn-tertiary" onClick={() => setPreviewIndex(null)}>
                Close
              </button>
            </div>

            <div className="preview-modal-body">
              <img src={currentPreview.url} alt={`Large submission preview ${previewIndex + 1}`} />
            </div>

            <div className="preview-modal-actions">
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={() => setPreviewIndex((current) => (current === null ? current : Math.max(0, current - 1)))}
                disabled={previewIndex <= 0}
              >
                Prev
              </button>
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={() =>
                  setPreviewIndex((current) =>
                    current === null ? current : Math.min(previewUrls.length - 1, current + 1)
                  )
                }
                disabled={previewIndex >= previewUrls.length - 1}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <DrawingCanvasModal
        isOpen={isDrawingOpen}
        onClose={() => setIsDrawingOpen(false)}
        onSave={(drawingFiles) => {
          emitReady(drawingFiles, { inputType: IMAGE_INPUT_TYPE.INK });
        }}
        questionMarkdown={questionMarkdown}
        initialDraftPages={drawingDraftPages}
        onDraftChange={onDrawingDraftChange}
        restoreFocusRef={drawingButtonRef}
      />
    </div>
  );
}

export default AnswerCapture;
